import { Button, PaginationProps, Popconfirm, Table, Typography } from 'antd';
import React, { useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from '../../i18n/i18n';
import './UsersTable.scss';
import { UserAccountStatus, UserRole } from '../../gen/proto/api/gateway/v1/user_pb';
import EditUserModal from './EditUserModal';
import { userAccountStatusLabel, userRoleLabel } from '../../utils/mapTranslationEnums';
import { LockOutlined, MinusCircleOutlined, PlusCircleOutlined, UnlockOutlined } from '@ant-design/icons';
import { userApi } from '../../api/user.api';
import useAuthStore from '../../stores/auth.store';
import EmptyState from '../utils/EmptyState/EmptyState';
import useTenantStore, { ALL_TENANTS_VALUE, TENANT_COL_KEY } from '../../stores/tenant.store';

const { Text } = Typography;

interface User {
  uuid: string;
  name: string;
  email: string;
  role: UserRole;
  accountStatus: UserAccountStatus;
  tenant?: string;
  tenantUuid?: string;
}

interface UsersTableProps {
  items: User[];
  loading?: boolean;
  pagination: PaginationProps;
  onChange: (pagination: PaginationProps) => void;
  onUserUpdated?: () => void;
}
const UsersTable = ({ items, loading, pagination, onChange, onUserUpdated }: UsersTableProps) => {
  const { t } = useTranslation('users', { keyPrefix: 'table' });
  const { t: tCommon } = useTranslation('common');
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const isAll = useTenantStore((state) => state.selectedTenant === ALL_TENANTS_VALUE);

  const columns = useMemo<ColumnsType<User>>(() => {
    const handleBlockClick = async (uuid: string, status: UserAccountStatus) => {
      try {
        status === UserAccountStatus.ACTIVE ? await userApi.blockUser(uuid) : await userApi.unblockUser(uuid);
        onUserUpdated?.();
      } catch (error) {
        console.error(error);
      }
    };

    return [
      {
        title: t('user'),
        dataIndex: 'name',
        key: 'name',
        width: '15%',
        render: (text: string) => <Text className={'MainTableText'}>{text}</Text>,
      },
      {
        title: t('mail'),
        dataIndex: 'email',
        key: 'email',
        width: '15%',
      },
      {
        title: t('tenant'),
        dataIndex: TENANT_COL_KEY,
        key: TENANT_COL_KEY,
        width: '15%',
      },
      {
        title: t('role'),
        dataIndex: 'role',
        key: 'role',
        width: '15%',
        render: (role: UserRole) => <Text>{userRoleLabel(role)}</Text>,
      },
      {
        title: t('state'),
        dataIndex: 'accountStatus',
        key: 'accountStatus',
        width: '10%',
        render: (status: UserAccountStatus) => {
          const active = status === UserAccountStatus.ACTIVE;
          return (
            <div className={'UsersTable__status'}>
              <div className={'UsersTable__status__items'} style={{ background: active ? 'green' : 'red' }} />
              <Text>{userAccountStatusLabel(status)}</Text>
            </div>
          );
        },
      },
      {
        title: t('action'),
        dataIndex: 'action',
        key: 'action',
        width: '5%',
        render: (_: string, record: User) => (
          <div className={'UsersTable__actions'} onClick={(e) => e.stopPropagation()}>
            <EditUserModal
              name={record.name}
              email={record.email}
              role={record.role}
              state={record.accountStatus}
              uuid={record.uuid}
              onSuccess={onUserUpdated}
              tenantUuid={record.tenantUuid}
            />
            <Popconfirm
              title={record.accountStatus === UserAccountStatus.ACTIVE ? t('blockUser') : t('unblockUser')}
              description={
                record.accountStatus === UserAccountStatus.ACTIVE ? t('blockUserAction') : t('unblockUserAction')
              }
              okText={t('yes')}
              cancelText={t('no')}
              placement={'left'}
              onConfirm={() => {
                handleBlockClick(record.uuid, record.accountStatus);
              }}
              icon={
                record.accountStatus === UserAccountStatus.ACTIVE ? (
                  <MinusCircleOutlined style={{ color: 'red' }} />
                ) : (
                  <PlusCircleOutlined style={{ color: 'green' }} />
                )
              }
            >
              <Button danger={record.accountStatus === UserAccountStatus.ACTIVE} type={'text'}>
                {record.accountStatus === UserAccountStatus.ACTIVE ? (
                  <LockOutlined />
                ) : (
                  <UnlockOutlined style={{ color: 'green' }} />
                )}
              </Button>
            </Popconfirm>
          </div>
        ),
      },
    ].filter(
      (col) => !(col.key === 'action' && !isSuperAdminOrCompanyAdmin) && !(col.key === TENANT_COL_KEY && !isAll),
    );
  }, [t, isSuperAdminOrCompanyAdmin, isAll, onUserUpdated]);

  return (
    <Table
      bordered={false}
      columns={columns}
      dataSource={items}
      loading={loading}
      pagination={{ ...pagination }}
      onChange={onChange}
      rowKey="uuid"
      locale={{ emptyText: <EmptyState title={tCommon('emptyTable')} /> }}
    />
  );
};

export default UsersTable;
